// SubmitNewPassword.js
import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import PasswordInput from './components/PasswordInput';
import ShowErrorMsg from './components/ShowErrorMsg';
import { serverUrl } from './ConfigHolder';

const SubmitNewPassword = ({ onPasswordChange, email }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');
  const [passwordSubmitError, setPasswordSubmitError] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [isVerifyCooldown, setIsVerifyCooldown] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const RESEND_COOLDOWN_MS = 5000;
  const VERIFY_COOLDOWN_MS = 5000;

  const handlePasswordChange = (e) => {
    const pw = e.target.value;
    setPassword(pw);
    setPasswordsMatch(pw === confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const confirmPw = e.target.value;
    setConfirmPassword(confirmPw);
    setPasswordsMatch(password === confirmPw);
  };

  const handleVerificationCodeChange = (e) => {
    const code = e.target.value;
    setVerificationCode(code.slice(0, 5)); // Limit to 5 digits
  };

  const handleSubmitPassword = async () => {
    setPasswordSubmitError('');
    const uri = `${serverUrl}/reset-password`;
    const response = await fetch(uri, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password, email }),
    });

    if (response.ok) {
      setIsCodeSent(true);
      setCanResend(false);
    } else {
      setPasswordSubmitError("Submission failed. Please, try again");
    }
  };

  const handleSubmitVerificationCode = async () => {
    setIsVerifyCooldown(true);
    setVerificationStatus('Verifying...');
    
    const uri = `${serverUrl}/verify-code`;
    const response = await fetch(uri, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: verificationCode, email }),
    });

    if (response.ok) {
      setIsVerifyCooldown(false);
      setVerificationStatus('Password reset successful!');
      setCanResend(false);
      if (onPasswordChange) {
        onPasswordChange(password);
      }
      // Introduce 3 sec delay
      await new Promise(resolve => setTimeout(resolve, 3000));
      setIsVerified(true);
      // Call login logic here. Not implemented. Maybe not needed. 2025-01-24 MB
    } else {
      const data = await response.json();
      setVerificationStatus(data.message);
      setTimeout(() => setIsVerifyCooldown(false), VERIFY_COOLDOWN_MS);
      setCanResend(true);
    }
  };

  const resendVerificationCode = async () => {
    setVerificationCode("");
    setVerificationStatus("");
    
    const uri = `${serverUrl}/resend-verify-code`;
    const response = await fetch(uri, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (response.ok) {
      setVerificationStatus('New verification code sent.');
      setCanResend(false);
      setTimeout(() => setCanResend(true), RESEND_COOLDOWN_MS);
    } else {
      const data = await response.json();
      setVerificationStatus(data.message);
      setTimeout(() => setIsVerifyCooldown(false), VERIFY_COOLDOWN_MS);
      setCanResend(true);
    }
  };

  return ( !isVerified &&
    <Box display="flex" flexDirection="column" alignItems="flex-start" mt={2} width="300px">
      {!isCodeSent ? (
        <>
          <PasswordInput
            value={password}
            onChange={handlePasswordChange}
            placeholder="Enter new password"
            name="new_password_unique"
            autocomplete='off'
          />
          <PasswordInput
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            placeholder="Confirm new password"
            name="confirm_password"
            autocomplete='off'
          />
          {!passwordsMatch && (
            <Typography color="error" variant="caption" mb={1}>
              Passwords do not match
            </Typography>
          )}
          {passwordSubmitError && <ShowErrorMsg error={passwordSubmitError} />}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitPassword}
            disabled={!passwordsMatch || password === ''}
            sx={{ mt: 1 }}
          >
            Submit New Password
          </Button>
        </>
      ) : (
        <>
          <Typography variant="body2" mb={2}>
            Verification code sent to your email. You can find it in your inbox 
            or <span style={{ color: 'red', fontWeight: 'bold' }}>SPAM folder.</span>
            <br />
            Please enter the 5-digit code:
          </Typography>
          <TextField
            type="text"
            value={verificationCode}
            onChange={handleVerificationCodeChange}
            placeholder="Enter verification code"
            variant="outlined"
            // variant = "standard"
            size="small"
            sx={{ mb: 2 }}
            slotProps={{
              input: { maxLength: 5 },
            }}
          />
          <Button
            // variant="outlined"
            variant="contained"
            color="primary"
            onClick={handleSubmitVerificationCode}
            disabled={verificationCode.length !== 5 || isVerifyCooldown}
          >
            {isVerifyCooldown ? 'Wait...' : 'Verify Code'}
          </Button>
          {verificationStatus && (
            <Typography variant="h6" mt={1} sx={{ fontWeight: 'bold' }}>
              {verificationStatus}
            </Typography>
          )}
          {canResend && (
            <Button
              variant="outlined"
              color="primary"
              onClick={resendVerificationCode}
              sx={{ mt: 1 }}
            >
              Resend Code
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default SubmitNewPassword;
